/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useMemo } from "react";
import {
	Box,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Checkbox,
	FormControlLabel,
	Typography,
	ListItemText,
	Radio,
	RadioGroup,
	FormLabel,
	Button,
	IconButton,
	Paper,
	Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	TreatmentConfigTemplate,
	CmVisitScheduleOption,
	RewardEngineOption,
	MobileMetadata,
} from "../../domain/treatmentPlan.interface";
import { Event } from "../../domain/event.interface";
import { CupResponse, IAnalyteInCup } from "../../domain/cup.interface";
import MobileMetadataSection from "./MobileMetadataSection";

interface TreatmentConfigFieldsProps {
	config: TreatmentConfigTemplate;
	onChange: (updatedConfig: TreatmentConfigTemplate) => void;
	options: {
		behaviors: Event[];
		durations: string[];
		visitsPerPeriod: string[];
		minHoursBetweenVisits: string[];
		cmVisitScheduleOptions: CmVisitScheduleOption[];
		rewardEngineOptions: RewardEngineOption[];
		cups?: CupResponse[];
	};
	showDefaultCheckbox?: boolean;
	showAllowCustomProviderSite?: boolean;
	isAdditionalConfig?: boolean;
}

const TreatmentConfigFields: FC<TreatmentConfigFieldsProps> = ({
	config,
	onChange,
	options,
	showDefaultCheckbox = false,
	showAllowCustomProviderSite = false,
	isAdditionalConfig = false,
}) => {
	// Get available analytes based on selected cups
	const availableAnalytes = useMemo(() => {
		if (!options.cups || !config.selectedCups?.length) {
			return [];
		}

		const selectedCupIds = config.selectedCups;
		const analytesMap = new Map<string, IAnalyteInCup>();

		options.cups
			.filter((cup) => selectedCupIds.includes(cup._id))
			.forEach((cup) => {
				cup.analytes.forEach((analyte) => {
					if (!analytesMap.has(analyte._id)) {
						analytesMap.set(analyte._id, analyte);
					}
				});
			});

		return Array.from(analytesMap.values());
	}, [options.cups, config.selectedCups]);

	const handleFieldChange = (
		field: keyof TreatmentConfigTemplate,
		value:
			| string
			| number
			| boolean
			| undefined
			| Record<string, unknown>
			| string[],
	) => {
		onChange({
			...config,
			[field]: value,
		});
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				p: isAdditionalConfig ? 2 : 0,
				border: isAdditionalConfig ? "1px solid #e0e0e0" : "none",
				borderRadius: isAdditionalConfig ? 1 : 0,
				backgroundColor: isAdditionalConfig ? "#f9f9f9" : "transparent",
			}}
		>
			{isAdditionalConfig && (
				<Typography variant="subtitle2" fontWeight="bold">
					Additional Activity Configuration
				</Typography>
			)}

			<TextField
				fullWidth
				label="Name"
				value={config.name || ""}
				onChange={(e) => handleFieldChange("name", e.target.value)}
				required
			/>

			<TextField
				fullWidth
				label="Segment"
				type="number"
				value={config.segment || ""}
				onChange={(e) => handleFieldChange("segment", Number(e.target.value))}
				required
			/>

			<FormControl fullWidth required>
				<InputLabel>Duration</InputLabel>
				<Select
					value={config.duration ?? ""}
					onChange={(e) =>
						handleFieldChange("duration", Number(e.target.value))
					}
					label="Duration"
				>
					{options.durations.map((duration) => (
						<MenuItem key={duration} value={duration}>
							{duration}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<TextField
				fullWidth
				label="Period Length"
				type="number"
				value={config.periodLength || ""}
				onChange={(e) =>
					handleFieldChange("periodLength", Number(e.target.value))
				}
				required
			/>

			<TextField
				fullWidth
				label="Range"
				value={config.range || ""}
				onChange={(e) => handleFieldChange("range", e.target.value)}
				required
			/>

			<FormControl fullWidth required>
				<InputLabel>Visits Per Period</InputLabel>
				<Select
					value={config.visitsPerPeriod || ""}
					onChange={(e) =>
						handleFieldChange("visitsPerPeriod", Number(e.target.value))
					}
					label="Visits Per Period"
				>
					{options.visitsPerPeriod.map((visits) => (
						<MenuItem key={visits} value={visits}>
							{visits}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<TextField
				fullWidth
				label="Custom Visit Count"
				type="number"
				value={config.customVisitsDuration ?? ""}
				onChange={(e) =>
					handleFieldChange(
						"customVisitsDuration",
						e.target.value === "" ? undefined : Number(e.target.value),
					)
				}
				inputProps={{ min: 1 }}
				helperText="Optional. Overrides the default total (Duration x Visits Per Period). Leave blank to use the default."
			/>

			<FormControl fullWidth required>
				<InputLabel>Min Hours Between Visits</InputLabel>
				<Select
					value={config.minHoursBetweenVisits ?? ""}
					onChange={(e) =>
						handleFieldChange("minHoursBetweenVisits", Number(e.target.value))
					}
					label="Min Hours Between Visits"
				>
					{options.minHoursBetweenVisits.map((hours) => (
						<MenuItem key={hours} value={hours}>
							{hours}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<TextField
				fullWidth
				label="Weekly Visits Per Period"
				type="number"
				value={config.weeklyVisitsPerPeriod || ""}
				onChange={(e) =>
					handleFieldChange("weeklyVisitsPerPeriod", Number(e.target.value))
				}
				required
			/>

			<FormControl fullWidth>
				<InputLabel>Reward Engine</InputLabel>
				<Select
					value={config.rewardEngineId || ""}
					onChange={(e) => handleFieldChange("rewardEngineId", e.target.value)}
					label="Reward Engine"
				>
					<MenuItem value="">
						<em>None</em>
					</MenuItem>
					{options.rewardEngineOptions.map((engine) => (
						<MenuItem key={engine.id} value={engine.id}>
							{engine.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{/* Reward Custom Configuration (Fishbowl) */}
			{config.rewardEngineId && (
				<Box
					sx={{
						border: "1px solid #e0e0e0",
						borderRadius: 1,
						p: 2,
						mt: 1,
					}}
				>
					<Typography variant="subtitle2" sx={{ mb: 2 }}>
						Reward Custom Configuration (Fishbowl)
					</Typography>

					<FormControl component="fieldset" sx={{ mb: 2 }}>
						<FormLabel component="legend">Fishbowl Type</FormLabel>
						<RadioGroup
							row
							value={(config as any).rewardCustomConfig?.fishbowlType || ""}
							onChange={(e) => {
								const fishbowlType = e.target.value;
								const currentConfig = (config as any).rewardCustomConfig || {};
								handleFieldChange("rewardCustomConfig", {
									...currentConfig,
									fishbowlType,
									// Clear prize distributions if switching to PHYSICAL
									...(fishbowlType === "PHYSICAL"
										? { prizeDistributions: undefined }
										: {}),
								});
							}}
						>
							<FormControlLabel
								value=""
								control={<Radio />}
								label="None (Money)"
							/>
							<FormControlLabel
								value="PHYSICAL"
								control={<Radio />}
								label="Physical Fishbowl"
							/>
							<FormControlLabel
								value="DIGITAL"
								control={<Radio />}
								label="Digital Fishbowl"
							/>
						</RadioGroup>
					</FormControl>

					{/* Digital Fishbowl Prize Distribution */}
					{(config as any).rewardCustomConfig?.fishbowlType === "DIGITAL" && (
						<Box>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									mb: 2,
								}}
							>
								<Typography variant="body2" fontWeight="bold">
									Prize Distributions
								</Typography>
								<Button
									size="small"
									startIcon={<AddIcon />}
									onClick={() => {
										const currentConfig =
											(config as any).rewardCustomConfig || {};
										const currentDistributions =
											currentConfig.prizeDistributions || [];
										handleFieldChange("rewardCustomConfig", {
											...currentConfig,
											prizeDistributions: [
												...currentDistributions,
												{
													amount: 0,
													probability: 0,
													displayMessage: "",
												},
											],
										});
									}}
								>
									Add Prize Tier
								</Button>
							</Box>

							{(
								(config as any).rewardCustomConfig?.prizeDistributions || []
							).map((dist: any, index: number) => (
								<Paper
									key={index}
									sx={{
										p: 2,
										mb: 2,
										bgcolor: "grey.50",
										position: "relative",
									}}
								>
									<IconButton
										size="small"
										sx={{ position: "absolute", top: 8, right: 8 }}
										onClick={() => {
											const currentConfig =
												(config as any).rewardCustomConfig || {};
											const currentDistributions =
												currentConfig.prizeDistributions || [];
											handleFieldChange("rewardCustomConfig", {
												...currentConfig,
												prizeDistributions: currentDistributions.filter(
													(_: any, i: number) => i !== index,
												),
											});
										}}
									>
										<DeleteIcon />
									</IconButton>

									<Grid container spacing={2}>
										<Grid item xs={12} md={4}>
											<TextField
												fullWidth
												size="small"
												label="Amount ($)"
												type="number"
												value={dist.amount || 0}
												onChange={(e) => {
													const currentConfig =
														(config as any).rewardCustomConfig || {};
													const currentDistributions = [
														...(currentConfig.prizeDistributions || []),
													];
													currentDistributions[index] = {
														...currentDistributions[index],
														amount: Number(e.target.value),
													};
													handleFieldChange("rewardCustomConfig", {
														...currentConfig,
														prizeDistributions: currentDistributions,
													});
												}}
											/>
										</Grid>
										<Grid item xs={12} md={4}>
											<TextField
												fullWidth
												size="small"
												label="Probability (%)"
												type="number"
												value={dist.probability || 0}
												onChange={(e) => {
													const currentConfig =
														(config as any).rewardCustomConfig || {};
													const currentDistributions = [
														...(currentConfig.prizeDistributions || []),
													];
													currentDistributions[index] = {
														...currentDistributions[index],
														probability: Number(e.target.value),
													};
													handleFieldChange("rewardCustomConfig", {
														...currentConfig,
														prizeDistributions: currentDistributions,
													});
												}}
												inputProps={{ min: 0, max: 100 }}
											/>
										</Grid>
										<Grid item xs={12}>
											<TextField
												fullWidth
												size="small"
												label="Display Message"
												value={dist.displayMessage || ""}
												onChange={(e) => {
													const currentConfig =
														(config as any).rewardCustomConfig || {};
													const currentDistributions = [
														...(currentConfig.prizeDistributions || []),
													];
													currentDistributions[index] = {
														...currentDistributions[index],
														displayMessage: e.target.value,
													};
													handleFieldChange("rewardCustomConfig", {
														...currentConfig,
														prizeDistributions: currentDistributions,
													});
												}}
												placeholder="e.g., Great! You earned $[AMOUNT] or Better luck next time!"
												helperText="Use [AMOUNT] placeholder for dynamic value"
											/>
										</Grid>
									</Grid>
								</Paper>
							))}

							{((config as any).rewardCustomConfig?.prizeDistributions || [])
								.length > 0 && (
								<Typography variant="caption" color="text.secondary">
									Total Probability:{" "}
									{(
										(config as any).rewardCustomConfig?.prizeDistributions || []
									)
										.reduce(
											(sum: number, d: any) => sum + (d.probability || 0),
											0,
										)
										.toFixed(1)}
									% (should be 100%)
								</Typography>
							)}
						</Box>
					)}
				</Box>
			)}

			<FormControl fullWidth>
				<InputLabel>Visit Schedule Options</InputLabel>
				<Select
					multiple
					value={
						config.cmVisitScheduleOptionIds
							? config.cmVisitScheduleOptionIds.split(",")
							: []
					}
					onChange={(e) => {
						const value = e.target.value as string[];
						handleFieldChange("cmVisitScheduleOptionIds", value.join(","));
					}}
					label="Visit Schedule Options"
					renderValue={(selected) => {
						const selectedArray = selected as string[];
						return selectedArray
							.map((id) => {
								const option = options.cmVisitScheduleOptions.find(
									(opt) => opt.cmVisitScheduleOptions.toString() === id,
								);
								return option?.cmVisitScheduleOptionsDaysDisplay || id;
							})
							.join(", ");
					}}
				>
					{options.cmVisitScheduleOptions.map((option) => (
						<MenuItem
							key={option.cmVisitScheduleOptions}
							value={option.cmVisitScheduleOptions.toString()}
						>
							<Checkbox
								checked={
									config.cmVisitScheduleOptionIds
										?.split(",")
										.includes(option.cmVisitScheduleOptions.toString()) || false
								}
							/>
							{option.cmVisitScheduleOptionsDaysDisplay}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{config.cmVisitScheduleOptionIds && (
				<FormControl fullWidth>
					<InputLabel>Default CM Visit Schedule</InputLabel>
					<Select
						value={
							config.cmVisitSchedule?.cmVisitScheduleOptions?.toString() || ""
						}
						onChange={(e) => {
							const selectedOption = options.cmVisitScheduleOptions.find(
								(opt) =>
									opt.cmVisitScheduleOptions.toString() === e.target.value,
							);
							if (selectedOption) {
								handleFieldChange("cmVisitSchedule", {
									cmVisitScheduleOptions: selectedOption.cmVisitScheduleOptions,
									cmVisitScheduleOptionsDays:
										selectedOption.cmVisitScheduleOptionsDays,
									cmVisitScheduleOptionsDaysDisplay:
										selectedOption.cmVisitScheduleOptionsDaysDisplay,
									cutDays: selectedOption.cutDays || [],
								});
							}
						}}
						label="Default CM Visit Schedule"
					>
						<MenuItem value="">
							<em>None</em>
						</MenuItem>
						{options.cmVisitScheduleOptions
							.filter((opt) =>
								config.cmVisitScheduleOptionIds
									?.split(",")
									.includes(opt.cmVisitScheduleOptions.toString()),
							)
							.map((option) => (
								<MenuItem
									key={option.cmVisitScheduleOptions}
									value={option.cmVisitScheduleOptions.toString()}
								>
									{option.cmVisitScheduleOptionsDaysDisplay}
								</MenuItem>
							))}
					</Select>
				</FormControl>
			)}

			<FormControl fullWidth required>
				<InputLabel>Behavior (Activity)</InputLabel>
				<Select
					value={config.behavior || ""}
					onChange={(e) => handleFieldChange("behavior", e.target.value)}
					label="Behavior (Activity)"
				>
					{options.behaviors.map((behavior) => (
						<MenuItem key={behavior._id} value={behavior._id}>
							{behavior.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<TextField
				fullWidth
				label="Disenrollment Days Threshold"
				type="number"
				value={config.disenrollmentDaysThreshold || ""}
				onChange={(e) =>
					handleFieldChange(
						"disenrollmentDaysThreshold",
						e.target.value ? Number(e.target.value) : undefined,
					)
				}
			/>

			{showDefaultCheckbox && (
				<FormControlLabel
					control={
						<Checkbox
							checked={config.default || false}
							onChange={(e) => handleFieldChange("default", e.target.checked)}
						/>
					}
					label="Set as Default"
				/>
			)}

			{showAllowCustomProviderSite && (
				<FormControlLabel
					control={
						<Checkbox
							checked={config.allowCustomProviderSite || false}
							onChange={(e) =>
								handleFieldChange("allowCustomProviderSite", e.target.checked)
							}
						/>
					}
					label="Allow Custom Provider Site (OU)"
				/>
			)}

			{/* Mobile App Configuration */}
			<MobileMetadataSection
				mobileMetadata={config.mobileMetadata}
				onChange={(mobileMetadata: MobileMetadata | undefined) =>
					onChange({ ...config, mobileMetadata })
				}
				eventName={
					options.behaviors.find((b) => b._id === config.behavior)?.name
				}
			/>

			{/* Cups and Analytes Configuration */}
			{options.cups && options.cups.length > 0 && (
				<>
					<FormControlLabel
						control={
							<Checkbox
								checked={config.useCupsAndAnalytes || false}
								onChange={(e) => {
									const isChecked = e.target.checked;
									// Batch all changes together to avoid stale state issues
									onChange({
										...config,
										useCupsAndAnalytes: isChecked,
										// Clear selections when disabling
										...(isChecked
											? {}
											: { selectedCups: [], primaryAnalytes: [] }),
									});
								}}
							/>
						}
						label="Use Cups and Primary Analytes"
					/>

					{config.useCupsAndAnalytes && (
						<>
							<FormControl fullWidth>
								<InputLabel>Selected Cups</InputLabel>
								<Select
									multiple
									value={config.selectedCups || []}
									onChange={(e) => {
										const value = e.target.value as string[];
										handleFieldChange("selectedCups", value);
										// Clear primary analytes that are no longer available
										if (config.primaryAnalytes?.length) {
											const newAvailableAnalyteIds = new Set<string>();
											options.cups
												?.filter((cup) => value.includes(cup._id))
												.forEach((cup) => {
													cup.analytes.forEach((a) =>
														newAvailableAnalyteIds.add(a._id),
													);
												});
											const validAnalytes = config.primaryAnalytes.filter(
												(id) => newAvailableAnalyteIds.has(id),
											);
											if (
												validAnalytes.length !== config.primaryAnalytes.length
											) {
												handleFieldChange("primaryAnalytes", validAnalytes);
											}
										}
									}}
									label="Selected Cups"
									renderValue={(selected) => {
										const selectedArray = selected as string[];
										return selectedArray
											.map((id) => {
												const cup = options.cups?.find((c) => c._id === id);
												return cup?.display || cup?.name || id;
											})
											.join(", ");
									}}
								>
									{options.cups.map((cup) => (
										<MenuItem key={cup._id} value={cup._id}>
											<Checkbox
												checked={
													config.selectedCups?.includes(cup._id) || false
												}
											/>
											<ListItemText primary={cup.display || cup.name} />
										</MenuItem>
									))}
								</Select>
							</FormControl>

							{config.selectedCups && config.selectedCups.length > 0 && (
								<FormControl fullWidth>
									<InputLabel>Primary Analytes</InputLabel>
									<Select
										multiple
										value={config.primaryAnalytes || []}
										onChange={(e) => {
											const value = e.target.value as string[];
											handleFieldChange("primaryAnalytes", value);
										}}
										label="Primary Analytes"
										renderValue={(selected) => {
											const selectedArray = selected as string[];
											return selectedArray
												.map((id) => {
													const analyte = availableAnalytes.find(
														(a) => a._id === id,
													);
													return (
														analyte?.shortnameDisplay ||
														analyte?.shortname ||
														id
													);
												})
												.join(", ");
										}}
									>
										{availableAnalytes.map((analyte) => (
											<MenuItem key={analyte._id} value={analyte._id}>
												<Checkbox
													checked={
														config.primaryAnalytes?.includes(analyte._id) ||
														false
													}
												/>
												<ListItemText
													primary={
														analyte.shortnameDisplay || analyte.shortname
													}
													secondary={analyte.scientificName}
												/>
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
						</>
					)}
				</>
			)}
		</Box>
	);
};

export default TreatmentConfigFields;
